import React, { useState, useMemo } from 'react';
import CalcShell from '../CalcShell';
import { CalcNetPanel, Row, UnitInput, FeetInches, RadioGroup, ResultTable } from '../_fitnessHelpers';

export default function PregnancyWeightGainCalc() {
  const [tab, setTab] = useState(0);
  const [ft, setFt] = useState(5);
  const [inches, setInches] = useState(6);
  const [lb, setLb] = useState(140);
  const [cm, setCm] = useState(168);
  const [kg, setKg] = useState(64);
  const [twin, setTwin] = useState('single');
  const [weeks, setWeeks] = useState(20);
  const clear = () => { setFt(5); setInches(6); setLb(140); setCm(168); setKg(64); setTwin('single'); setWeeks(20); };

  const c = useMemo(() => {
    const h = tab === 0 ? ((+ft||0)*12+(+inches||0))*0.0254 : (+cm||0)/100;
    const w = tab === 0 ? (+lb||0)*0.453592 : +kg||0;
    const bmi = h > 0 ? w/(h*h) : 0;
    let minTotal, maxTotal, category;
    if (bmi < 18.5)     { minTotal = 12.5; maxTotal = 18.0; category = 'Underweight'; }
    else if (bmi < 25)   { minTotal = 11.5; maxTotal = 16.0; category = 'Normal weight'; }
    else if (bmi < 30)   { minTotal = 7.0;  maxTotal = 11.5; category = 'Overweight'; }
    else                  { minTotal = 5.0;  maxTotal = 9.0;  category = 'Obesity'; }
    if (twin === 'twin') {
      if (bmi < 18.5)     { minTotal = 15.0; maxTotal = 22.0; }
      else if (bmi < 25)  { minTotal = 17.0; maxTotal = 25.0; }
      else if (bmi < 30)  { minTotal = 14.0; maxTotal = 23.0; }
      else                { minTotal = 11.0; maxTotal = 19.0; }
    }
    const t1End = 13;
    const perWeekMin = (minTotal - 1) / 27;
    const perWeekMax = (maxTotal - 1) / 27;
    const wkGainMin = weeks <= t1End ? weeks * 0.5/13 : 0.5 + (weeks - t1End) * perWeekMin;
    const wkGainMax = weeks <= t1End ? weeks * 2.0/13 : 2.0 + (weeks - t1End) * perWeekMax;
    const disp = v => tab === 0 ? `${(v/0.453592).toFixed(1)} lb` : `${v.toFixed(1)} kg`;
    return { bmi, category, minTotal, maxTotal, wkGainMin, wkGainMax, disp };
  }, [tab, ft, inches, lb, cm, kg, twin, weeks]);

  return (
    <CalcShell look="cnet" title="Pregnancy Weight Gain Calculator" subtitle="Healthy weight-gain ranges during pregnancy (IOM 2009 guidelines).">
      <CalcNetPanel slug="pwg" activeTab={tab} onTabChange={setTab} onClear={clear} right={
        <div>
          <div className="text-lg text-slate-800">Pre-pregnancy BMI</div>
          <div className="text-2xl font-bold text-[#2f9c3f]" data-testid="pwg-bmi">{c.bmi.toFixed(2)}</div>
          <div className="text-sm font-medium text-slate-700">Category: {c.category}</div>
          <ResultTable testid="pwg-detail-table" rows={[
            ['Total recommended gain (40 wk)', <span className="font-bold text-[#2f9c3f]">{c.disp(c.minTotal)} – {c.disp(c.maxTotal)}</span>],
            [`Recommended gain at week ${weeks}`, `${c.disp(c.wkGainMin)} – ${c.disp(c.wkGainMax)}`],
          ]}/>
          {/* Full week-by-week weight-gain schedule */}
          <div className="mt-4">
            <h4 className="font-bold text-slate-900 mb-2 text-sm">Week-by-week Weight-Gain Schedule (IOM guidelines)</h4>
            <div className="overflow-x-auto max-h-96 border border-slate-300 rounded">
              <table className="w-full text-xs" data-testid="pwg-weekly-table">
                <thead className="bg-[#1e40af] text-white sticky top-0">
                  <tr>
                    <th className="px-2 py-1.5 text-left">Week</th>
                    <th className="px-2 py-1.5 text-left">Trimester</th>
                    <th className="px-2 py-1.5 text-right">Min gain</th>
                    <th className="px-2 py-1.5 text-right">Max gain</th>
                    <th className="px-2 py-1.5 text-right">Est. Weight</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: 40 }, (_, i) => i + 1).map((w) => {
                    const perWeekMin = (c.minTotal - 1) / 27;
                    const perWeekMax = (c.maxTotal - 1) / 27;
                    const gMin = w <= 13 ? w * 0.5 / 13 : 0.5 + (w - 13) * perWeekMin;
                    const gMax = w <= 13 ? w * 2.0 / 13 : 2.0 + (w - 13) * perWeekMax;
                    const important = [4, 8, 12, 16, 20, 24, 28, 32, 36, 37, 40].includes(w);
                    const isCurrent = w === +weeks;
                    const cls = isCurrent ? 'bg-emerald-100 font-bold' : important ? 'bg-amber-50' : (w % 2 ? 'bg-slate-50' : '');
                    const baseW = tab === 0 ? (+lb || 0) : (+kg || 0);
                    const dispMid = ((gMin + gMax) / 2);
                    return (
                      <tr key={w} className={cls}>
                        <td className="px-2 py-1">Week {w}{isCurrent ? ' ★' : ''}{important ? ' ●' : ''}</td>
                        <td className="px-2 py-1">{w < 13 ? '1st' : w < 27 ? '2nd' : '3rd'}</td>
                        <td className="px-2 py-1 text-right font-mono">{c.disp(gMin)}</td>
                        <td className="px-2 py-1 text-right font-mono">{c.disp(gMax)}</td>
                        <td className="px-2 py-1 text-right font-mono">{tab === 0 ? (baseW + dispMid / 0.453592).toFixed(1) + ' lb' : (baseW + dispMid).toFixed(1) + ' kg'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="text-xs text-slate-500 mt-1">
              ● Important weeks · ★ Current · Highlighted rows are IOM milestone weeks.
            </div>
          </div>
        </div>
      }>
        {tab === 0 ? <>
          <Row label="Pre-preg. Height"><div className="grid grid-cols-2 gap-2 flex-1"><FeetInches ft={ft} inches={inches} onFt={setFt} onIn={setInches} testidPrefix="pwg-h"/></div></Row>
          <Row label="Pre-preg. Weight"><UnitInput value={lb} onChange={setLb} unit="pounds" testid="pwg-w"/></Row>
        </> : <>
          <Row label="Pre-preg. Height"><UnitInput value={cm} onChange={setCm} unit="cm" testid="pwg-hcm"/></Row>
          <Row label="Pre-preg. Weight"><UnitInput value={kg} onChange={setKg} unit="kg" testid="pwg-wkg"/></Row>
        </>}
        <Row label="Pregnancy"><RadioGroup name="pwg-twin" value={twin} onChange={setTwin} options={[{value:'single',label:'Single'},{value:'twin',label:'Twins'}]}/></Row>
        <Row label="Current week"><UnitInput value={weeks} onChange={setWeeks} unit="weeks" testid="pwg-weeks"/></Row>
      </CalcNetPanel>
    </CalcShell>
  );
}
